import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MeData } from 'graphQL/Auth/types';
import { useUserInformation } from 'graphQL/Zustand/useUserInformation';
import HackleManager from 'utils/hackle';
import { formatOrderStatus } from 'utils/hackle/parameterFormatting';
import { useQueryGetOrders } from '../graphQL/hooks/useQueryGetOrders';
import { OrderFilterInput } from '../graphQL/types';
import { ItemType, OrderState } from 'types/api';
import { useQueryGetDelivery } from 'containers/Delivery/graphQL/hooks/useQueryGetDelivery';

export interface SaleFilterValue {
  searchValue: string;
  termType: string;
  searchType: string;
}

export interface TermWithType {
  startedAt: number;
  endedAt: number;
}

export const useSaleListHooks = () => {
  const location = useLocation();
  const myInfo: MeData = useUserInformation(state => state.userInfo);

  const { deliveryData } = useQueryGetDelivery();
  const { orderItems, isLoading, setIsLoading, handleRefetchOrderItems } =
    useQueryGetOrders();
  const [orderFilterInput, setOrderFilterInput] = useState<OrderFilterInput>({
    page: 1,
    size: 10,
    filter: {
      hostIds: myInfo.me.host?.id,
      productIds: location.state?.productId ? [location.state.productId] : null,
      itemTypeIn: [ItemType.FRIP, ItemType.DIGITAL],
      productTitleLike: null,
      customerIds: null,
      itemNameLike: null,
      itemIds: null,
      emailLike: null,
      nameLike: null,
      phoneLike: null,
      statusIn: null,
      transactionStatusIn: null,
      orderIds: null,
      scheduleIds: null,
    },
  });

  const [isAcceptFilter, setIsAcceptFilter] = useState<boolean>(
    location.state ? true : false,
  );
  const [isOpenCountFilter, setIsOpenCountFilter] = useState<boolean>(false);
  const [isOpenSearchModal, setIsOpenSearchModal] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [filterValue, setFilterValue] = useState<SaleFilterValue>({
    searchValue: location.state ? location.state.productId : '',
    termType: 'all',
    searchType: location.state ? 'fripId' : 'productName',
  });
  const [termWithType, setTermWithType] = useState<TermWithType | null>(null);

  const handleSetFilterValue = (key: string, value: any) => {
    setFilterValue({ ...filterValue, [key]: value });
  };

  const handleSetTermWithType = (type: string) => {
    const today = new Date();

    switch (type) {
      case 'all': {
        setTermWithType(null);
        return;
      }
      case 'today': {
        const startedAt = new Date(today);
        startedAt.setHours(0, 0, 0, 0);

        setTermWithType({
          startedAt: startedAt.getTime(),
          endedAt: today.getTime(),
        });
        return;
      }
      case '1month': {
        const startedAt = new Date(today);
        startedAt.setMonth(startedAt.getMonth() - 1);

        setTermWithType({
          startedAt: startedAt.getTime(),
          endedAt: today.getTime(),
        });
        return;
      }
      case '6month': {
        const startedAt = new Date(today);
        startedAt.setMonth(startedAt.getMonth() - 1);

        setTermWithType({
          startedAt: startedAt.getTime(),
          endedAt: today.getTime(),
        });
        return;
      }
      case '1year': {
        const startedAt = new Date(today);
        startedAt.setFullYear(startedAt.getFullYear() - 1);

        setTermWithType({
          startedAt: startedAt.getTime(),
          endedAt: today.getTime(),
        });
        return;
      }
      case '5year': {
        const startedAt = new Date(today);
        startedAt.setFullYear(startedAt.getFullYear() - 5);

        setTermWithType({
          startedAt: startedAt.getTime(),
          endedAt: today.getTime(),
        });
        return;
      }
      default:
        return;
    }
  };

  const handleSetOrderFilterInput = (key: string, value: any) => {
    setOrderFilterInput({ ...orderFilterInput, page: 1, [key]: value });
  };

  const handleIsOpenCountFilter = () => {
    setIsOpenCountFilter(!isOpenCountFilter);
  };

  const handleResetFilterValue = () => {
    setFilterValue({
      searchValue: '',
      termType: 'all',
      searchType: 'productName',
    });
  };

  const handleRefreshData = () => {
    handleRefetchOrderItems(orderFilterInput);
  };

  useEffect(() => {
    if (orderItems) {
      handleRefreshData();
      setSelectedId('');
      setIsLoading(true);

      HackleManager.logSalesView(
        orderItems.totalCount,
        orderFilterInput.page,
        formatOrderStatus(orderFilterInput.filter.statusIn),
      );
    }
  }, [orderFilterInput]);

  return {
    orderItems,
    isLoading,
    orderFilterInput,
    isOpenCountFilter,
    isOpenSearchModal,
    selectedId,
    filterValue,
    termWithType,
    isAcceptFilter,
    deliveryData,
    handleSetTermWithType,
    handleSetFilterValue,
    setSelectedId,
    handleSetOrderFilterInput,
    setIsOpenSearchModal,
    handleIsOpenCountFilter,
    handleResetFilterValue,
    setIsAcceptFilter,
    handleRefreshData,
  };
};
